import type React from 'react';
import { Divider, FieldSet, FormField, Input, Label, Legend } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { TRASH_REMOVAL } from '../../../../constants';
import HeaderToggle from './HeaderToggle';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';
import CurrencyInput from '../../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function TrashRemoval_v3({ section, setSectionValues }: Props) {
  const trashRemoval_isEnabled = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const fixedAmountValue = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function onToggleUtility() {
    setSectionValues({
      [TRASH_REMOVAL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED),
    });
  }

  function onServiceProviderChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setSectionValues({
      [TRASH_REMOVAL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY]: event.currentTarget.value,
    });
  }

  function setFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [TRASH_REMOVAL.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Trash removal"
        subheader="Tenant pays in addition to base rent"
        switchOn={section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED)}
        onSwitchToggle={onToggleUtility}
      />
      {trashRemoval_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <FieldSet required legend={<Legend>Who will be responsible for trash removal set up?</Legend>}>
            <RadioListBox
              section={section}
              setSectionValues={setSectionValues}
              option1FieldName={TRASH_REMOVAL.FIELD_NAME.TENANT_RESPONSIBILITY}
              option2FieldName={TRASH_REMOVAL.FIELD_NAME.LANDLORD_RESPONSIBILITY}
              option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
              option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
              option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
              option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
            />
          </FieldSet>
          {tenantSelected && (
            <FormField
              required
              css={{ marginTop: 'layout.tight' }}
              label={<Label>What service provider?</Label>}
              control={
                <Input
                  value={section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY)}
                  onChange={onServiceProviderChange}
                  data-test="service-provider"
                />
              }
            />
          )}
          {landlordSelected && (
            <FormField
              required
              css={{ marginTop: 'layout.tight' }}
              label={<Label>What is the additional fixed fee for trash removal?</Label>}
              control={
                <CurrencyInput
                  aria-label="fixed amount"
                  data-test="fixedAmountValue"
                  value={fixedAmountValue || ''}
                  onBlur={setFixedAmountValue}
                  onChange={setFixedAmountValue}
                  endAdornment="per month"
                />
              }
            />
          )}
        </>
      )}
    </>
  );
}
